import { ToastType } from '@components/toast';
import { NoteAttachmentPostRequestInformationArray, NotePostRequestBody, NotePostResponse } from '@lio/api-types';
import { NoteType } from '@lio/rule-engine';
import { PostArtifact } from '@models/note';
import { messageQueryKeys } from '@services/note/messages/messageQueryKeys';
import { getNoteMessagesParams } from '@services/note/note.helpers';

import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';
import { useSnackbar } from 'notistack';

/** Note: quoteId is truly optional but typescript types do not handle 'anyof' well
 *  anyOf: [{ required: ["quoteId"] }, { required: ["policyId"] }],
 *  => is requiring the first item to be required, when it should in reality be either or.
 */
type NotePostRequestBodyWithOptionalQuoteId = Omit<NotePostRequestBody, 'quoteId'> & {
  quoteId?: string;
};

type InsertNoteAsyncParams = {
  noteRequestBody: NotePostRequestBodyWithOptionalQuoteId;
  artifacts?: PostArtifact[];
};

export const useInsertNote = (handleScrollMessagesToBottom = () => {}) => {
  const queryClient = useQueryClient();

  const client = useClient();
  const { enqueueSnackbar } = useSnackbar();
  const mutationFn = async ({ noteRequestBody, artifacts }: InsertNoteAsyncParams) => {
    const {
      quoteId,
      type,
      body,
      brokerageId,
      customerId,
      parentId,
      ccEmails,
      documentIds,
      artifacts: artifactsToCopy,
      replacesNoteId,
      isPinned,
      rawText,
      policyId,
    } = noteRequestBody;

    if (artifacts?.length) {
      // post new note as formData with attachments
      const formData = new FormData();
      const attachmentsInformation: NoteAttachmentPostRequestInformationArray = [];
      artifacts.forEach((f) => {
        formData.append('files', f.file);
        attachmentsInformation.push({
          name: f.name,
          description: f.description,
        });
      });
      formData.append(`attachmentsInformation`, JSON.stringify(attachmentsInformation));
      const note = {
        quoteId,
        policyId,
        type,
        brokerageId,
        customerId,
        parentId,
        body,
        ccEmails,
        documentIds,
        rawText,
        isPinned: isPinned ?? false,
        replacesNoteId,
        artifacts: artifactsToCopy,
      };
      formData.append('note', JSON.stringify(note));

      return client
        .post(`note`, {
          body: formData,
        })
        .json<NotePostResponse>(); // in useUploadArtifacts it is NoteAttachmentPostResponse
    }
    // post new note as json
    const postResult = await client
      .post('note', {
        json: {
          quoteId,
          policyId,
          type,
          brokerageId,
          customerId,
          parentId,
          body,
          rawText,
          ccEmails,
          documentIds,
          artifacts: artifactsToCopy,
          replacesNoteId,
          isPinned,
        },
      })
      .json<NotePostResponse>();
    return postResult;
  };
  const result = useMutation<NotePostResponse, Error, InsertNoteAsyncParams>({
    mutationFn,
    onSuccess: async (noteResponse) => {
      const queryParams = getNoteMessagesParams(noteResponse.quoteId, noteResponse.type as NoteType);

      queryClient.invalidateQueries({ queryKey: ['notes'] });

      queryClient.invalidateQueries({ queryKey: queryParams }).then(() => {
        handleScrollMessagesToBottom?.();
      });

      // Clear all Messages
      queryClient.invalidateQueries({ queryKey: [messageQueryKeys.resource] });
    },
    onError: async (e, params) => {
      const message = await getApiErrorMessage(e, `${params.noteRequestBody.type} insertion failed`);
      enqueueSnackbar(message, {
        preventDuplicate: true,
        variant: ToastType.Error,
        persist: true,
      });
    },
  });
  return { ...result, insertNoteAsync: result.mutateAsync };
};
